import { useTranslations } from "@jugl-web/utils";
import { useShowAlert } from "@jugl-web/domain-resources/ui-components";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import BrokenHeartAnimationData from "./assets/broken-heart-animation.json";
import SuccessAnimationData from "./assets/success-animation.json";
import { InviteError } from "../../types/InviteError";

export const useHandleInvitationError = () => {
  const { t } = useTranslations();
  const showAlert = useShowAlert();
  const isInviteError = (error: InviteError) => {
    return [
      InviteError.ErrorMaxSeatsExceeded,
      InviteError.UserAlreadyExists,
      InviteError.InviteExpired,
      InviteError.SubscriptionExpired,
      InviteError.EntityNotActive,
    ].includes(error);
  };
  const handleError = (
    type: "link" | "invite",
    error: InviteError,
    onClose?: () => void
  ) => {
    if (!isInviteError(error)) {
      return;
    }
    let title = "";
    let content: string | React.ReactNode = "";
    let animation: unknown = null;
    switch (error) {
      case InviteError.UserAlreadyExists:
        title = t({
          id: "short-links-page.already-member-of-workspace",
          defaultMessage: "You’re already a member of this workspace 🫡​",
        });
        content = t({
          id: "short-links-page.already-member-of-workspace-description",
          defaultMessage: "You already have access – no need to join again.",
        });
        animation = SuccessAnimationData;
        break;
      case InviteError.SubscriptionExpired:
        title = "Workspace subscription ended 😱";
        content = (
          <>
            The workspace you&apos;re trying to join is currently inactive due
            to a lapsed subscription.
            <br />
            <br />
            <span className="text-primary-500">
              please contact the workspace admin to resolve this issue.
            </span>
          </>
        );
        break;
      case InviteError.ErrorMaxSeatsExceeded:
        title = "No seats available 😱";
        content = (
          <>
            The workspace you&apos;re trying to join has reached its member
            limit.
            <br />
            <br />
            <span className="text-primary-500">
              To join, please contact the workspace admin and ask them to add
              more seats.
            </span>
          </>
        );
        break;
      default:
        title = t({
          id: "short-links-page.invite-expired",
          defaultMessage: "Invite link broken 😱",
        });
        content = (
          <>
            {t({
              id: "short-links-page.invite-expired-description",
              defaultMessage:
                "It looks like this invite link is no longer valid. It might have expired, been revoked, or maybe the workspace was deleted.",
            })}
            <br />
            <br />
            <span className="text-primary-500">
              please contact the workspace admin for a new invitation.
            </span>
          </>
        );
        break;
    }
    showAlert({
      title,
      content,
      img: (
        <Lottie
          options={{
            animationData: animation || BrokenHeartAnimationData,
          }}
          width={362}
          height={221}
        />
      ),
      onClosed: () => {
        onClose?.();
      },
      buttons: [
        {
          role: "close",
          color: "primary",
          onClick: (_, { closeAlert }) => {
            onClose?.();
            closeAlert();
          },
          text: t({
            id: "common.okay",
            defaultMessage: "Okay",
          }),
        },
      ],
    });
  };

  return {
    handleError,
    isInviteError,
  };
};
